import { useState } from "react";
import type { Branch, PriceList } from "@probetalab/contracts";
import { run } from "../lib/api.js";
import { useToast } from "../lib/toast.js";
import { errorMessage } from "../lib/useQuery.js";
import { Alert, Button, Card, Chip, Field, Input, Select, Sheet } from "./ui.js";

/*
 * Catálogo → Listas de precios (pilot request of 2026-11-02, 19:32: "¿Se puede tener dos listas
 * de precios, una para el Capulín y otra para SJ?"). Every Administrador action is one command:
 * «Copiar lista» `catalogue.price_list.duplicate`, «Renombrar» / «Quitar»
 * `catalogue.price_list.update`, the list of each branch `branch.price_list.set` (migration
 * 0018). «Editar precios» opens the studies tab on that list. A branch without a list of its own
 * uses the general (default) list; orders already registered keep their prices.
 */

type Dialog = { kind: "copy"; list: PriceList } | { kind: "rename"; list: PriceList } | { kind: "remove"; list: PriceList } | null;

/** "" in the branch selector = no list of its own (the general list). */
const GENERAL = "";

export function PriceListsPanel({
  lists,
  priceCount,
  branches,
  admin,
  onChanged,
  onBranchesChanged,
  onEditPrices,
}: {
  lists: PriceList[];
  priceCount: (listId: string) => number;
  branches: Branch[];
  admin: boolean;
  onChanged: () => void;
  onBranchesChanged: () => Promise<void> | void;
  onEditPrices: (listId: string) => void;
}) {
  const toast = useToast();
  const [dialog, setDialog] = useState<Dialog>(null);
  const [saving, setSaving] = useState<string | null>(null);
  const active = lists.filter((l) => l.is_active).sort((a, b) => Number(b.is_default) - Number(a.is_default) || a.name.localeCompare(b.name));
  const general = active.find((l) => l.is_default) ?? null;
  const usedBy = (l: PriceList) => branches.filter((b) => (b.price_list_id ?? general?.id) === l.id);

  async function assign(branch: Branch, value: string) {
    setSaving(branch.id);
    try {
      await run("branch.price_list.set", { branch_id: branch.id, price_list_id: value === GENERAL ? null : value });
      const name = value === GENERAL ? (general?.name ?? "la lista general") : (active.find((l) => l.id === value)?.name ?? "");
      toast.success(`${branch.name} cobra con ${name}.`);
      await onBranchesChanged();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setSaving(null);
    }
  }

  const done = () => {
    setDialog(null);
    onChanged();
  };

  return (
    <div className="stack-lg">
      <Card title="Listas de precios" pad={false}>
        {active.map((l) => {
          const using = usedBy(l);
          return (
            <div key={l.id} className="list-row wrap" data-testid={`price-list-${l.id}`}>
              <span className="grow">
                <span className="primary">{l.name}</span> {l.is_default && <Chip tone="blue">General</Chip>}
                <span className="secondary" style={{ display: "block" }}>
                  {priceCount(l.id)} precios · {using.length ? `La usa ${using.map((b) => b.name).join(" y ")}` : "Ninguna sucursal la usa"}
                </span>
              </span>
              <span className="row wrap" style={{ gap: 8 }}>
                <Button size="sm" variant="secondary" icon="tag" onClick={() => onEditPrices(l.id)}>
                  {admin ? "Editar precios" : "Ver precios"}
                </Button>
                {admin && (
                  <>
                    <Button size="sm" variant="ghost" icon="pen" onClick={() => setDialog({ kind: "rename", list: l })}>
                      Renombrar
                    </Button>
                    <Button size="sm" variant="ghost" icon="plus" onClick={() => setDialog({ kind: "copy", list: l })}>
                      Copiar lista
                    </Button>
                    {!l.is_default && (
                      <Button size="sm" variant="ghost" icon="trash" onClick={() => setDialog({ kind: "remove", list: l })}>
                        Quitar
                      </Button>
                    )}
                  </>
                )}
              </span>
            </div>
          );
        })}
      </Card>

      <Card title="Lista de cada sucursal" pad={false}>
        <p className="small muted" style={{ padding: "12px 18px 0" }}>
          Recepción y Cotizaciones cobran con la lista de la sucursal. Las solicitudes ya registradas conservan sus precios.
        </p>
        {branches.map((b) => (
          <div key={b.id} className="list-row wrap">
            <span className="grow primary">{b.name}</span>
            {admin ? (
              <Select
                aria-label={`Lista de precios de ${b.name}`}
                value={b.price_list_id && active.some((l) => l.id === b.price_list_id) ? b.price_list_id : GENERAL}
                disabled={saving !== null}
                onChange={(e) => void assign(b, e.target.value)}
                style={{ width: 300 }}
              >
                <option value={GENERAL}>{general ? `${general.name} (general)` : "Lista general"}</option>
                {active
                  .filter((l) => !l.is_default)
                  .map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.name}
                    </option>
                  ))}
              </Select>
            ) : (
              <span className="muted">{active.find((l) => l.id === b.price_list_id)?.name ?? general?.name ?? "—"}</span>
            )}
          </div>
        ))}
      </Card>

      <Sheet
        open={dialog !== null}
        onClose={() => setDialog(null)}
        title={dialog?.kind === "copy" ? `Copiar ${dialog.list.name}` : dialog?.kind === "rename" ? `Renombrar ${dialog.list.name}` : dialog ? `Quitar ${dialog.list.name}` : ""}
      >
        {dialog?.kind === "copy" && <CopyListForm key={dialog.list.id} list={dialog.list} onCancel={() => setDialog(null)} onSaved={done} />}
        {dialog?.kind === "rename" && <RenameListForm key={dialog.list.id} list={dialog.list} onCancel={() => setDialog(null)} onSaved={done} />}
        {dialog?.kind === "remove" && <RemoveListForm key={dialog.list.id} list={dialog.list} usedBy={usedBy(dialog.list)} onCancel={() => setDialog(null)} onSaved={done} />}
      </Sheet>
    </div>
  );
}

function NameForm({ label, initial, hint, action, onSubmit, onCancel }: { label: string; initial: string; hint?: string; action: string; onSubmit: (name: string) => Promise<void>; onCancel: () => void }) {
  const [name, setName] = useState(initial);
  const [busy, setBusy] = useState(false);
  const n = name.trim();
  async function submit() {
    if (!n) return;
    setBusy(true);
    try {
      await onSubmit(n);
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="stack" style={{ gap: 16 }}>
      <Field label={label} hint={hint}>
        <Input aria-label={label} value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && void submit()} autoFocus maxLength={120} />
      </Field>
      <div className="row end">
        <Button variant="ghost" onClick={onCancel}>
          Cancelar
        </Button>
        <Button variant="primary" icon="check" busy={busy} disabled={!n || n === initial} onClick={() => void submit()}>
          {action}
        </Button>
      </div>
    </div>
  );
}

function CopyListForm({ list, onSaved, onCancel }: { list: PriceList; onSaved: () => void; onCancel: () => void }) {
  const toast = useToast();
  return (
    <NameForm
      label="Nombre de la lista nueva"
      initial=""
      hint={`Empieza con los mismos precios que ${list.name}; después cambia los que quieras.`}
      action="Copiar lista"
      onCancel={onCancel}
      onSubmit={async (name) => {
        try {
          await run("catalogue.price_list.duplicate", { source_price_list_id: list.id, name, company_id: null });
          toast.success(`Lista creada: ${name}.`);
          onSaved();
        } catch (e) {
          toast.error(errorMessage(e));
        }
      }}
    />
  );
}

function RenameListForm({ list, onSaved, onCancel }: { list: PriceList; onSaved: () => void; onCancel: () => void }) {
  const toast = useToast();
  return (
    <NameForm
      label="Nombre de la lista"
      initial={list.name}
      action="Guardar nombre"
      onCancel={onCancel}
      onSubmit={async (name) => {
        try {
          await run("catalogue.price_list.update", { price_list_id: list.id, name, is_active: true });
          toast.success(`Lista renombrada: ${name}.`);
          onSaved();
        } catch (e) {
          toast.error(errorMessage(e));
        }
      }}
    />
  );
}

function RemoveListForm({ list, usedBy, onSaved, onCancel }: { list: PriceList; usedBy: Branch[]; onSaved: () => void; onCancel: () => void }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  async function remove() {
    setBusy(true);
    try {
      await run("catalogue.price_list.update", { price_list_id: list.id, name: list.name, is_active: false });
      toast.success(`Lista quitada: ${list.name}.`);
      onSaved();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="stack" style={{ gap: 16 }}>
      {usedBy.length ? (
        <Alert tone="amber">
          La usa {usedBy.map((b) => b.name).join(" y ")}. Asigna otra lista a esa sucursal antes de quitarla.
        </Alert>
      ) : (
        <p>Deja de aparecer para cotizar y registrar. Las solicitudes ya registradas con esta lista conservan sus precios.</p>
      )}
      <div className="row end">
        <Button variant="ghost" onClick={onCancel}>
          Cancelar
        </Button>
        <Button variant="danger" icon="trash" busy={busy} disabled={usedBy.length > 0} onClick={() => void remove()}>
          Quitar lista
        </Button>
      </div>
    </div>
  );
}
